import OnboardingTip from '@rio-cloud/rio-uikit/OnboardingTip';
import RadioCardGroup, { type RadioCardOption } from '@rio-cloud/rio-uikit/RadioCardGroup';
import { useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';

import type { RootState } from '../../../../configuration/setup/store';
import { formatDurationUnit } from '../../common/utils/formatDurationUnit';
import {
    checkoutActions,
    getFullyDiscountedLevel,
    getProductLevelsForVariant,
    getSelectedProductLevel,
} from '../redux/checkout.redux';
import type { ProductLevel } from '../redux/types';

interface Props {
    levelsOfVariant?: ProductLevel[];
    setSelectedProductLevel: (level: string) => void;
    selectedProductLevel?: string;
    fullyDiscountedLevel?: string;
}

function getContractDuration(productLevel: ProductLevel, intl: IntlShape) {
    return productLevel.contractPeriod !== undefined
        ? `${productLevel.contractPeriod.length} ${formatDurationUnit(productLevel.contractPeriod.unit, intl, productLevel.contractPeriod.length)}`
        : intl.formatMessage({ id: 'marketplace.productDetails.evergreen' });
}

export const VariantSelection = ({
    levelsOfVariant,
    setSelectedProductLevel,
    selectedProductLevel,
    fullyDiscountedLevel,
}: Props) => {
    const intl = useIntl();
    const [hideOnboarding, setHideOnboarding] = useState(false);
    if (!levelsOfVariant) {
        return null;
    }
    const highestPrice = Math.max(...levelsOfVariant.map(productLevel => productLevel.price));
    const options: RadioCardOption[] = levelsOfVariant.map(productLevel => {
        const saving = Math.round((1 - productLevel.price / highestPrice) * 100);

        return {
            value: productLevel.level,
            className: 'flex-1-1-0 text-center',
            label: (
                <OnboardingTip
                    show={fullyDiscountedLevel === productLevel.level && !hideOnboarding}
                    placement='bottom'
                    width={350}
                    textAlignment='center'
                    content={intl.formatMessage({ id: 'marketplace.checkout.levelDiscountedForOneResource' })}
                    onHide={() => setHideOnboarding(true)}
                >
                    <span>{getContractDuration(productLevel, intl)}</span>
                </OnboardingTip>
            ),
            description:
                saving > 0 ? <FormattedMessage id='marketplace.saving' values={{ percentage: saving }} /> : undefined,
        };
    });

    return (
        <div className='display-flex justify-content-center padding-bottom-15 z-index-1 position-relative'>
            <form className='width-60pct'>
                <RadioCardGroup
                    name='productLevel'
                    options={options}
                    value={selectedProductLevel}
                    onChange={nextValue => {
                        if (selectedProductLevel !== nextValue) {
                            setSelectedProductLevel(nextValue);
                        }
                    }}
                    variant='stacked-horizontal'
                />
            </form>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    levelsOfVariant: getProductLevelsForVariant(state),
    selectedProductLevel: getSelectedProductLevel(state),
    fullyDiscountedLevel: getFullyDiscountedLevel(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    setSelectedProductLevel: (level: string) => dispatch(checkoutActions.setSelectedProductLevel(level)),
});

export const VariantSelectionContainer = connect(mapStateToProps, mapDispatchToProps)(VariantSelection);
